import { useState, useEffect, useRef, useCallback, type MouseEvent, type ChangeEvent, type DragEvent } from 'react'
import { XMarkIcon, ArrowUpTrayIcon, CircleStackIcon, EyeIcon, EyeSlashIcon, DocumentIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { Button } from '@/components/common/Button'
import { notify } from '@/utils/notify'
import { createDatasources, type Agent, type DataSourceCreate } from '@/services/api'
import {
  decryptDatasourcesFromImport,
  readImportFile,
  type DatasourceExportData
} from '@/utils/datasourceExport'
import { useQueryClient } from '@tanstack/react-query'

interface ImportConnectionsModalProps {
  isOpen: boolean
  onClose: () => void
  agents: Agent[]
}

type ImportStep = 'file' | 'password' | 'preview' | 'complete'

interface ImportPreviewItem extends DatasourceExportData {
  matchedAgent: Agent | null
  agentAutoAssigned: boolean
  willImport: boolean
  importPassword: string
  showPassword: boolean
}

interface ImportResult {
  name: string
  success: boolean
  error?: string
}

export function ImportConnectionsModal({ isOpen, onClose, agents }: ImportConnectionsModalProps) {
  const [step, setStep] = useState<ImportStep>('file')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileContent, setFileContent] = useState<string>('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string>('')
  const [previewData, setPreviewData] = useState<ImportPreviewItem[]>([])
  const [importResults, setImportResults] = useState<ImportResult[]>([])

  const modalRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('file')
      setSelectedFile(null)
      setFileContent('')
      setPassword('')
      setShowPassword(false)
      setError('')
      setPreviewData([])
      setImportResults([])
    }
  }, [isOpen])

  // Check if all selected connections that require passwords have them
  const getConnectionsMissingPasswords = useCallback(() => {
    return previewData.filter(item => {
      if (!item.willImport) return false
      // Memcached doesn't need password
      if (item.databaseType === 'Memcached') {
        return false
      }
      // Redis (standalone and cluster) password is optional
      if (item.databaseType === 'Redis' || item.databaseType === 'RedisCluster') {
        return false
      }
      // Other database types require password
      return !item.importPassword
    })
  }, [previewData])

  const connectionsMissingPasswords = getConnectionsMissingPasswords()
  const hasValidationErrors = connectionsMissingPasswords.length > 0 ||
    previewData.some(item => item.willImport && !item.agentId)

  // Focus password input when entering password step
  useEffect(() => {
    if (step === 'password') {
      setTimeout(() => {
        passwordInputRef.current?.focus()
      }, 100)
    }
  }, [step])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleModalClick = useCallback((e: MouseEvent) => {
    e.stopPropagation()
  }, [])

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.name.endsWith('.json')) {
      setError('Please select a JSON file')
      return
    }

    setSelectedFile(file)
    setError('')

    try {
      const content = await readImportFile(file)
      setFileContent(content)
      setStep('password')
    } catch {
      setError('Failed to read file')
    }
  }

  const handleDecrypt = async () => {
    if (!password) {
      setError('Please enter the password')
      return
    }

    setIsProcessing(true)
    setError('')

    try {
      const decryptedData = await decryptDatasourcesFromImport(fileContent, password)

      // Match decrypted data with existing agents
      const activeAgents = agents.filter(a => a.is_active !== false)
      const preview: ImportPreviewItem[] = decryptedData.map(item => {
        const matchedAgent = item.agentId
          ? agents.find(a => a.id === item.agentId) ?? null
          : null
        // Auto-select first active agent if original agent not found
        const fallbackAgent = !matchedAgent && activeAgents.length > 0
          ? activeAgents[0]
          : null
        const effectiveAgent = matchedAgent ?? fallbackAgent ?? null
        return { ...item,
          agentId: effectiveAgent?.id ?? item.agentId,
          matchedAgent: effectiveAgent,
          agentAutoAssigned: !matchedAgent && fallbackAgent !== null,
          willImport: true,
          importPassword: item.password || '',
          showPassword: false }
      })

      setPreviewData(preview)
      setStep('preview')
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message)
      } else {
        setError('Failed to decrypt file')
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const toggleImportItem = (index: number) => {
    setPreviewData(prev =>
      prev.map((item, i) =>
        i === index
          ? { ...item, willImport: !item.willImport }
          : item
      )
    )
  }

  const updateImportPassword = (index: number, newPassword: string) => {
    setPreviewData(prev =>
      prev.map((item, i) =>
        i === index
          ? { ...item, importPassword: newPassword }
          : item
      )
    )
  }

  const togglePasswordVisibility = (index: number) => {
    setPreviewData(prev =>
      prev.map((item, i) =>
        i === index
          ? { ...item, showPassword: !item.showPassword }
          : item
      )
    )
  }

  const updateAgentId = (index: number, agentId: string) => {
    setPreviewData(prev =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              agentId,
              matchedAgent: agents.find(a => a.id === agentId) ?? null
            }
          : item
      )
    )
  }

  const handleImport = async () => {
    setIsProcessing(true)

    const results: ImportResult[] = []

    try {
      for (const item of previewData) {
        if (!item.willImport) {
          continue
        }

        // Skip if no agent is selected
        if (!item.agentId) {
          results.push({
            name: item.displayName,
            success: false,
            error: 'No agent selected'
          })
          continue
        }

        // Determine if password is required (not for Redis/Memcached without auth)
        const needsPassword = item.databaseType !== 'Memcached' &&
                              item.databaseType !== 'Redis'

        // Check if password is required and missing
        if (needsPassword && !item.importPassword && item.databaseType !== 'Redis') {
          results.push({
            name: item.displayName,
            success: false,
            error: 'Password is required'
          })
          continue
        }

        try {
          // Create datasource via API
          const createPayload: {
            displayName: string
            databaseType: 'PostgreSQL' | 'MySQL' | 'MSSQL' | 'Redis' | 'RedisCluster' | 'MongoDB' | 'Cassandra' | 'SQLite' | 'OracleDB' | 'Memcached' | 'ClickHouse' | 'ScyllaDB' | 'Milvus'
            host: string
            port: number
            username: string
            password?: string
            agentId: string
            defaultDatabase?: string | null
            connectionParams?: Record<string, unknown> | null
            isLocked?: boolean
          } = {
            displayName: item.displayName,
            databaseType: item.databaseType,
            host: item.host,
            port: item.port,
            username: item.username || (item.databaseType === 'Memcached' ? 'default' : ''),
            agentId: item.agentId,
            defaultDatabase: item.defaultDatabase,
            connectionParams: item.connectionParams,
            isLocked: item.isLocked
          }

          // Only include password for non-memcached connections
          if (item.databaseType !== 'Memcached') {
            createPayload.password = item.importPassword || ''
          }

          await createDatasources({ body: createPayload as DataSourceCreate, throwOnError: true })

          results.push({
            name: item.displayName,
            success: true
          })
        } catch (err) {
          results.push({
            name: item.displayName,
            success: false,
            error: err instanceof Error ? err.message : 'Failed to create connection'
          })
        }
      }

      setImportResults(results)
      setStep('complete')

      // Invalidate datasources query to refresh the list
      await queryClient.invalidateQueries({ queryKey: ['datasources'] })

      // Clear sensitive data after import
      setPassword('')
      setFileContent('')

      const successCount = results.filter(r => r.success).length
      if (successCount > 0) {
        notify.success(`Successfully imported ${successCount} connection(s)`)
      }
    } catch (error) {
      console.error('Import failed:', error)
      notify.error('Failed to import connections')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const file = e.dataTransfer.files[0]
    if (!file) return

    if (!file.name.endsWith('.json')) {
      setError('Please drop a JSON file')
      return
    }

    setSelectedFile(file)
    setError('')

    try {
      const content = await readImportFile(file)
      setFileContent(content)
      setStep('password')
    } catch {
      setError('Failed to read file')
    }
  }

  if (!isOpen) return null

  const successCount = importResults.filter(r => r.success).length
  const failedCount = importResults.filter(r => !r.success).length

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-modal-title"
        className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-h-[90vh] flex flex-col"
        onClick={handleModalClick}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/20">
              <ArrowUpTrayIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <h3 id="import-modal-title" className="text-lg font-medium text-gray-900 dark:text-white">
              Import Connections
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Step 1: File Selection */}
          {step === 'file' && (
            <>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-sm text-blue-700 dark:text-blue-300">
                <p className="flex items-start">
                  <CircleStackIcon className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  Import database connections from a previously exported file.
                </p>
              </div>

              <div
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-400 dark:hover:border-blue-500 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDrop={(e) => void handleDrop(e)}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={(e) => void handleFileSelect(e)}
                  className="hidden"
                />
                <DocumentIcon className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="text-blue-600 dark:text-blue-400 font-medium">Click to select</span>
                  {' '}or drag and drop
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  JSON export file only
                </p>
              </div>

              {error && (
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
            </>
          )}

          {/* Step 2: Password Entry */}
          {step === 'password' && (
            <>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <div className="flex items-center">
                  <DocumentIcon className="w-5 h-5 text-gray-500 mr-2" />
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                    {selectedFile?.name}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Decryption Password
                </label>
                <div className="relative">
                  <input
                    ref={passwordInputRef}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      setError('')
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        void handleDecrypt()
                      }
                    }}
                    placeholder="Enter the password used during export..."
                    className="block w-full px-3 py-2 pr-10 rounded-md border border-gray-300 shadow-sm transition-colors focus:border-gray-500 dark:focus:border-gray-400 focus:ring-0 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
                {error && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
                )}
              </div>
            </>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && (
            <>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Review the connections to import. You can update passwords and agent assignments before importing.
              </div>

              <div className="border border-gray-200 dark:border-gray-600 rounded-lg max-h-80 overflow-y-auto">
                {previewData.map((item, index) => (
                  <div
                    key={index}
                    className={`p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                      !item.willImport ? 'bg-gray-50 dark:bg-gray-800/50 opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={item.willImport}
                        onChange={() => toggleImportItem(index)}
                        className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-0 dark:border-gray-600 dark:bg-gray-700"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.displayName}
                          </span>
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                            ({item.databaseType})
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {item.host}:{item.port} - {item.username || 'no username'}
                        </div>

                        {/* Agent status */}
                        <div className="mt-2">
                          {item.matchedAgent && !item.agentAutoAssigned ? (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded">
                              <CheckCircleIcon className="w-3 h-3 mr-1" />
                              Agent: {item.matchedAgent.name}
                            </span>
                          ) : (
                            <div className="space-y-1">
                              <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${
                                item.agentId
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                              }`}>
                                <ExclamationCircleIcon className="w-3 h-3 mr-1" />
                                {item.agentId ? 'Original agent not found - auto-assigned' : 'Agent not found - select one'}
                              </span>
                              {item.willImport && (
                                <select
                                  value={item.agentId || ''}
                                  onChange={(e) => updateAgentId(index, e.target.value)}
                                  className="block w-full text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                >
                                  <option value="">Select an agent...</option>
                                  {agents.filter(a => a.is_active !== false).map(agent => (
                                    <option key={agent.id} value={agent.id}>
                                      {agent.name}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Password input for connections that need it */}
                        {item.willImport && item.databaseType !== 'Memcached' && (
                          <div className="mt-2">
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                              Password {item.databaseType === 'Redis' || item.databaseType === 'RedisCluster' ? '(optional)' : '(required)'}
                            </label>
                            <div className="relative">
                              <input
                                type={item.showPassword ? 'text' : 'password'}
                                value={item.importPassword}
                                onChange={(e) => updateImportPassword(index, e.target.value)}
                                placeholder={item.password ? 'Password included from export' : 'Enter password...'}
                                className="block w-full px-2 py-1 pr-8 text-sm rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                              />
                              <button
                                type="button"
                                onClick={() => togglePasswordVisibility(index)}
                                className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                              >
                                {item.showPassword ? <EyeSlashIcon className="w-3 h-3" /> : <EyeIcon className="w-3 h-3" />}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-sm text-gray-500 dark:text-gray-400">
                {previewData.filter(i => i.willImport).length} of {previewData.length} connections will be imported
              </div>

              {/* Validation errors */}
              {connectionsMissingPasswords.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                  <p className="font-medium mb-1">Password required for:</p>
                  <ul className="list-disc list-inside text-xs">
                    {connectionsMissingPasswords.map((item, idx) => (
                      <li key={idx}>{item.displayName} ({item.databaseType})</li>
                    ))}
                  </ul>
                </div>
              )}

              {previewData.some(item => item.willImport && !item.agentId) && (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                  <p className="font-medium mb-1">Agent required for:</p>
                  <ul className="list-disc list-inside text-xs">
                    {previewData.filter(item => item.willImport && !item.agentId).map((item, idx) => (
                      <li key={idx}>{item.displayName}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* Step 4: Complete */}
          {step === 'complete' && (
            <div className="py-4">
              <div className="text-center mb-6">
                <CheckCircleIcon className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Import Complete
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {successCount > 0 && `Successfully imported ${successCount} connection(s)`}
                  {successCount > 0 && failedCount > 0 && ', '}
                  {failedCount > 0 && `${failedCount} failed`}
                </p>
              </div>

              {/* Results list */}
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg max-h-48 overflow-y-auto">
                {importResults.map((result, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                      result.success ? '' : 'bg-red-50 dark:bg-red-900/10'
                    }`}
                  >
                    <span className="text-sm text-gray-900 dark:text-white">
                      {result.name}
                    </span>
                    {result.success ? (
                      <span className="inline-flex items-center text-xs text-green-600 dark:text-green-400">
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                        Success
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-xs text-red-600 dark:text-red-400" title={result.error}>
                        <ExclamationCircleIcon className="w-4 h-4 mr-1" />
                        Failed
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 dark:border-gray-700">
          {step === 'file' && (
            <Button variant="secondary" onClick={onClose} size="sm">
              Cancel
            </Button>
          )}

          {step === 'password' && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setStep('file')
                  setSelectedFile(null)
                  setFileContent('')
                  setPassword('')
                  setError('')
                }}
                size="sm"
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleDecrypt()}
                loading={isProcessing}
                disabled={!password || isProcessing}
                size="sm"
              >
                Decrypt
              </Button>
            </>
          )}

          {step === 'preview' && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setStep('password')
                  setPreviewData([])
                }}
                size="sm"
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleImport()}
                loading={isProcessing}
                disabled={!previewData.some(i => i.willImport) || isProcessing || hasValidationErrors}
                size="sm"
              >
                Import Connections
              </Button>
            </>
          )}

          {step === 'complete' && (
            <Button variant="primary" onClick={onClose} size="sm">
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
